/**
 * Shop Page - Shelly Button E-commerce
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ShoppingCart, Package, ChevronLeft, ChevronRight, Minus, Plus, Loader2, CreditCard, MapPin, Phone, Mail, User } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../../components/ui/card';
import { shopAPI } from '../../services/api';
import { useAuthStore } from '../../store';

const Shop = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuthStore();
  
  // State
  const [products, setProducts] = useState([]);
  const [sliderImages, setSliderImages] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isSandbox, setIsSandbox] = useState(false);
  
  // Direct checkout mode (from profile page)
  const directCheckout = searchParams.get('checkout') === 'true';
  const preselectedProduct = searchParams.get('product') || 'shelly_user';
  const preselectedQuantity = parseInt(searchParams.get('qty') || '1', 10);
  
  // Cart state
  const [selectedProduct, setSelectedProduct] = useState(preselectedProduct);
  const [quantity, setQuantity] = useState(preselectedQuantity);
  const [showCheckout, setShowCheckout] = useState(directCheckout);
  
  // Checkout form
  const [formData, setFormData] = useState({
    payer_name: user?.first_name || '',
    payer_surname: user?.last_name || '',
    payer_email: user?.email || '',
    payer_phone: user?.phone || '',
    street_name: '',
    street_number: '',
    apartment: '',
    zip_code: '',
    city: '',
    state: '',
  });
  
  // Load data
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const [productsRes, sliderRes] = await Promise.all([
          shopAPI.getProducts(),
          shopAPI.getSliderImages()
        ]);
        
        // Public shop only shows the player button ($750 MXN)
        // Club button ($500) is only available in club owner back-office
        let allProducts = productsRes.data.products || [];
        allProducts = allProducts.filter(p => p.id === 'shelly_user');
        
        setProducts(allProducts);
        setSliderImages(sliderRes.data.images || []);
        setIsSandbox(productsRes.data.is_sandbox || false);
        
        // Select the only available product
        if (allProducts.length === 1) {
          setSelectedProduct(allProducts[0].id);
        }
      } catch (err) {
        console.error('Error loading shop data:', err);
        setError('Error al cargar la tienda');
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, []);
  
  // Auto-advance slider
  useEffect(() => {
    if (sliderImages.length > 1) {
      const interval = setInterval(() => {
        setCurrentSlide(prev => (prev + 1) % sliderImages.length);
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [sliderImages.length]);
  
  // Update form when user data loads
  useEffect(() => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        payer_name: user.first_name || prev.payer_name,
        payer_surname: user.last_name || prev.payer_surname,
        payer_email: user.email || prev.payer_email,
        payer_phone: user.phone || prev.payer_phone,
      }));
    }
  }, [user]);
  
  // Get selected product
  const getProduct = () => products.find(p => p.id === selectedProduct);
  const product = getProduct();
  const totalPrice = product ? product.price * quantity : 0;
  
  // Handle form change
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };
  
  // Handle checkout
  const handleCheckout = async (e) => {
    e.preventDefault();
    
    // Validate form
    const required = ['payer_name', 'payer_surname', 'payer_email', 'payer_phone', 'street_name', 'street_number', 'zip_code', 'city', 'state'];
    const missing = required.filter(field => !formData[field]);
    
    if (missing.length > 0) {
      setError('Por favor completa todos los campos requeridos');
      return;
    }
    
    try {
      setCheckoutLoading(true);
      setError(null);
      
      const checkoutData = {
        product_type: selectedProduct,
        quantity,
        payer_name: formData.payer_name,
        payer_surname: formData.payer_surname,
        payer_email: formData.payer_email,
        payer_phone: formData.payer_phone,
        shipping_address: {
          street_name: formData.street_name,
          street_number: formData.street_number,
          apartment: formData.apartment,
          zip_code: formData.zip_code,
          city: formData.city,
          state: formData.state,
          country: 'México'
        }
      };
      
      const response = await shopAPI.createCheckout(checkoutData);
      
      if (response.data.checkout_url) {
        // Redirect to Mercado Pago
        window.location.href = response.data.checkout_url;
      } else {
        setError('Error al crear la preferencia de pago');
      }
    } catch (err) {
      console.error('Checkout error:', err);
      setError(err.response?.data?.detail || 'Error al procesar el pago');
    } finally {
      setCheckoutLoading(false);
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#7ED957]" />
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gray-900">
      {/* Header */}
      <header className="bg-gray-800/80 border-b border-gray-700 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate(-1)} className="text-gray-400 hover:text-white">
              <ChevronLeft className="w-6 h-6" />
            </button>
            <h1 className="text-xl font-bold text-white">Tienda Clipper</h1>
          </div>
          
          {isSandbox && (
            <span className="px-2 py-1 bg-yellow-500/20 text-yellow-400 text-xs rounded-full">
              Modo Sandbox
            </span>
          )}
        </div>
      </header>
      
      <main className="max-w-7xl mx-auto px-4 py-6">
        {!showCheckout ? (
          <>
            {/* Slider */}
            {sliderImages.length > 0 && (
              <div className="relative mb-8 rounded-xl overflow-hidden">
                <div className="aspect-[21/9] bg-gray-800 relative">
                  {sliderImages.map((img, idx) => (
                    <div
                      key={img.id || idx}
                      className={`absolute inset-0 transition-opacity duration-500 ${idx === currentSlide ? 'opacity-100' : 'opacity-0'}`}
                    >
                      {img.image_data ? (
                        <img
                          src={img.image_data.startsWith('data:') ? img.image_data : `data:image/png;base64,${img.image_data}`}
                          alt={img.title}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-r from-[#7ED957]/20 to-gray-800 flex items-center justify-center">
                          <div className="text-center p-8">
                            <Package className="w-16 h-16 text-[#7ED957] mx-auto mb-4" />
                            <h3 className="text-2xl font-bold text-white mb-2">{img.title}</h3>
                            <p className="text-gray-400">{img.subtitle}</p>
                          </div>
                        </div>
                      )}
                      
                      {/* Overlay text */}
                      {img.title && (
                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-6">
                          <h3 className="text-2xl font-bold text-white">{img.title}</h3>
                          {img.subtitle && <p className="text-gray-300">{img.subtitle}</p>}
                        </div>
                      )}
                    </div>
                  ))}
                  
                  {/* Slider controls */}
                  <button
                    onClick={() => setCurrentSlide(prev => (prev - 1 + sliderImages.length) % sliderImages.length)}
                    className="absolute left-4 top-1/2 -translate-y-1/2 p-2 bg-black/50 rounded-full text-white hover:bg-black/70"
                  >
                    <ChevronLeft className="w-6 h-6" />
                  </button>
                  <button
                    onClick={() => setCurrentSlide(prev => (prev + 1) % sliderImages.length)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 p-2 bg-black/50 rounded-full text-white hover:bg-black/70"
                  >
                    <ChevronRight className="w-6 h-6" />
                  </button>
                  
                  {/* Dots */}
                  <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                    {sliderImages.map((_, idx) => (
                      <button
                        key={idx}
                        onClick={() => setCurrentSlide(idx)}
                        className={`w-2 h-2 rounded-full transition-colors ${idx === currentSlide ? 'bg-[#7ED957]' : 'bg-white/50'}`}
                      />
                    ))}
                  </div>
                </div>
              </div>
            )}
            
            {/* Product - Single product for public shop */}
            <Card className="bg-gray-800 border-gray-700 mb-6">
              <CardHeader>
                <CardTitle className="text-white text-xl">{products[0]?.title}</CardTitle>
                <CardDescription className="text-gray-400">
                  {products[0]?.description}
                </CardDescription>
              </CardHeader>
              <CardFooter className="border-t border-gray-700 pt-4 flex items-center justify-between">
                <div className="flex items-baseline gap-1">
                  <span className="text-3xl font-bold text-[#7ED957]">${products[0]?.price?.toLocaleString()}</span>
                  <span className="text-gray-400">{products[0]?.currency}</span>
                </div>
                <span className="text-sm text-gray-500">IVA incluido</span>
              </CardFooter>
            </Card>
            
            {/* Quantity selector */}
            <Card className="bg-gray-800 border-gray-700 mb-6">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-medium text-white">Cantidad</h3>
                    <p className="text-gray-400 text-sm">Máximo 10 unidades por pedido</p>
                  </div>
                  <div className="flex items-center gap-4">
                    <button
                      onClick={() => setQuantity(q => Math.max(1, q - 1))}
                      className="p-2 bg-gray-700 rounded-lg text-white hover:bg-gray-600 disabled:opacity-50"
                      disabled={quantity <= 1}
                    >
                      <Minus className="w-5 h-5" />
                    </button>
                    <span className="text-2xl font-bold text-white w-12 text-center">{quantity}</span>
                    <button
                      onClick={() => setQuantity(q => Math.min(10, q + 1))}
                      className="p-2 bg-gray-700 rounded-lg text-white hover:bg-gray-600 disabled:opacity-50"
                      disabled={quantity >= 10}
                    >
                      <Plus className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            {/* Total and checkout button */}
            <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
              <div className="flex items-center justify-between mb-4">
                <span className="text-gray-400">Total</span>
                <span className="text-3xl font-bold text-white">${totalPrice.toLocaleString()} MXN</span>
              </div>
              <Button
                onClick={() => setShowCheckout(true)}
                className="w-full bg-[#7ED957] hover:bg-[#6bc548] text-black font-bold py-6 text-lg"
              >
                <ShoppingCart className="w-5 h-5 mr-2" />
                Continuar al pago
              </Button>
            </div>
          </>
        ) : (
          /* Checkout Form */
          <div className="max-w-2xl mx-auto">
            <button
              onClick={() => setShowCheckout(false)}
              className="flex items-center text-gray-400 hover:text-white mb-6"
            >
              <ChevronLeft className="w-5 h-5 mr-1" />
              Volver a la tienda
            </button>
            
            <Card className="bg-gray-800 border-gray-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <CreditCard className="w-5 h-5 text-[#7ED957]" />
                  Información de pago
                </CardTitle>
                <CardDescription className="text-gray-400">
                  Completa tus datos para proceder con Mercado Pago
                </CardDescription>
              </CardHeader>
              
              <form onSubmit={handleCheckout}>
                <CardContent className="space-y-6">
                  {error && (
                    <div className="p-3 bg-red-500/20 border border-red-500/50 rounded-lg text-red-400 text-sm">
                      {error}
                    </div>
                  )}
                  
                  {/* Order summary */}
                  <div className="p-4 bg-gray-700/50 rounded-lg">
                    <h4 className="font-medium text-white mb-2">Resumen del pedido</h4>
                    <div className="flex justify-between text-gray-300">
                      <span>{product?.title} x {quantity}</span>
                      <span>${totalPrice.toLocaleString()} MXN</span>
                    </div>
                  </div>
                  
                  {/* Personal info */}
                  <div>
                    <h4 className="font-medium text-white mb-3 flex items-center gap-2">
                      <User className="w-4 h-4 text-[#7ED957]" />
                      Datos personales
                    </h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-gray-300">Nombre *</Label>
                        <Input
                          name="payer_name"
                          value={formData.payer_name}
                          onChange={handleInputChange}
                          className="bg-gray-700 border-gray-600 text-white"
                          placeholder="Juan"
                          required
                        />
                      </div>
                      <div>
                        <Label className="text-gray-300">Apellido *</Label>
                        <Input
                          name="payer_surname"
                          value={formData.payer_surname}
                          onChange={handleInputChange}
                          className="bg-gray-700 border-gray-600 text-white"
                          placeholder="García"
                          required
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4 mt-4">
                      <div>
                        <Label className="text-gray-300 flex items-center gap-1">
                          <Mail className="w-3 h-3" /> Email *
                        </Label>
                        <Input
                          name="payer_email"
                          type="email"
                          value={formData.payer_email}
                          onChange={handleInputChange}
                          className="bg-gray-700 border-gray-600 text-white"
                          placeholder="juan@email.com"
                          required
                        />
                      </div>
                      <div>
                        <Label className="text-gray-300 flex items-center gap-1">
                          <Phone className="w-3 h-3" /> Teléfono *
                        </Label>
                        <Input
                          name="payer_phone"
                          value={formData.payer_phone}
                          onChange={handleInputChange}
                          className="bg-gray-700 border-gray-600 text-white"
                          placeholder="5512345678"
                          required
                        />
                      </div>
                    </div>
                  </div>
                  
                  {/* Shipping address */}
                  <div>
                    <h4 className="font-medium text-white mb-3 flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-[#7ED957]" />
                      Dirección de envío
                    </h4>
                    <div className="space-y-4">
                      <div className="grid grid-cols-3 gap-4">
                        <div className="col-span-2">
                          <Label className="text-gray-300">Calle *</Label>
                          <Input
                            name="street_name"
                            value={formData.street_name}
                            onChange={handleInputChange}
                            className="bg-gray-700 border-gray-600 text-white"
                            placeholder="Av. Insurgentes Sur"
                            required
                          />
                        </div>
                        <div>
                          <Label className="text-gray-300">Número *</Label>
                          <Input
                            name="street_number"
                            value={formData.street_number}
                            onChange={handleInputChange}
                            className="bg-gray-700 border-gray-600 text-white"
                            placeholder="123"
                            required
                          />
                        </div>
                      </div>
                      <div>
                        <Label className="text-gray-300">Apartamento / Interior</Label>
                        <Input
                          name="apartment"
                          value={formData.apartment}
                          onChange={handleInputChange}
                          className="bg-gray-700 border-gray-600 text-white"
                          placeholder="Depto 4B (opcional)"
                        />
                      </div>
                      <div className="grid grid-cols-3 gap-4">
                        <div>
                          <Label className="text-gray-300">C.P. *</Label>
                          <Input
                            name="zip_code"
                            value={formData.zip_code}
                            onChange={handleInputChange}
                            className="bg-gray-700 border-gray-600 text-white"
                            placeholder="06600"
                            maxLength={5}
                            required
                          />
                        </div>
                        <div>
                          <Label className="text-gray-300">Ciudad *</Label>
                          <Input
                            name="city"
                            value={formData.city}
                            onChange={handleInputChange}
                            className="bg-gray-700 border-gray-600 text-white"
                            placeholder="Ciudad de México"
                            required
                          />
                        </div>
                        <div>
                          <Label className="text-gray-300">Estado *</Label>
                          <Input
                            name="state"
                            value={formData.state}
                            onChange={handleInputChange}
                            className="bg-gray-700 border-gray-600 text-white"
                            placeholder="CDMX"
                            required
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
                
                <CardFooter className="flex-col gap-4 border-t border-gray-700 pt-6">
                  <div className="w-full flex items-center justify-between text-lg">
                    <span className="text-gray-400">Total a pagar:</span>
                    <span className="text-2xl font-bold text-[#7ED957]">${totalPrice.toLocaleString()} MXN</span>
                  </div>
                  <Button
                    type="submit"
                    disabled={checkoutLoading}
                    className="w-full bg-[#009ee3] hover:bg-[#0088cc] text-white font-bold py-6 text-lg"
                  >
                    {checkoutLoading ? (
                      <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Procesando...</>
                    ) : (
                      <><CreditCard className="w-5 h-5 mr-2" /> Pagar con Mercado Pago</>
                    )}
                  </Button>
                  <p className="text-xs text-gray-500 text-center">
                    Serás redirigido a Mercado Pago para completar tu compra de forma segura
                  </p>
                </CardFooter>
              </form>
            </Card>
          </div>
        )}
      </main>
    </div>
  );
};

export default Shop;
